import { Component, type ReactNode } from 'react';
import KoobLoadError from './KoobLoadError';

type KoobErrorBoundaryProps = {
  children: ReactNode;
  /** The boundary clears when it changes: a page that crashed lets the user
   * navigate away. */
  resetKey?: string;
  /** Around the whole app: the error fills the screen, and trying again
   * reloads it. */
  fullScreen?: boolean;
};

type KoobErrorBoundaryState = {
  failed: boolean;
  /** React.lazy keeps a chunk that failed to load (a deploy since the app
   * opened) as failed: only reloading the page fetches it again. */
  chunkFailed?: boolean;
  resetKey?: string;
};

/** Without it, an error thrown while rendering unmounts the whole app and
 * leaves a blank screen (KD-3991). */
export default class KoobErrorBoundary extends Component<
  KoobErrorBoundaryProps,
  KoobErrorBoundaryState
> {
  state: KoobErrorBoundaryState = {
    failed: false,
    resetKey: this.props.resetKey,
  };

  static getDerivedStateFromError(error: unknown) {
    return {
      failed: true,
      chunkFailed: (error as Error | undefined)?.name === 'ChunkLoadError',
    };
  }

  static getDerivedStateFromProps(
    props: KoobErrorBoundaryProps,
    state: KoobErrorBoundaryState,
  ) {
    return props.resetKey === state.resetKey
      ? null
      : { failed: false, chunkFailed: false, resetKey: props.resetKey };
  }

  retry = () => {
    if (this.props.fullScreen || this.state.chunkFailed) {
      window.location.reload();
    } else this.setState({ failed: false });
  };

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <KoobLoadError onRetry={this.retry} fullScreen={this.props.fullScreen} />
    );
  }
}
